import { useEffect, useState, type ReactNode } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Menu, Monitor, Moon, Sun } from 'lucide-react';
import { atlas, setCsrfToken, type TreeResponse } from '../lib/api';
import { useTheme, type ThemeMode } from '../lib/theme';
import { useGenerationUpdates } from '../lib/updates';
import { GlobalSearch } from './GlobalSearch';
import { CatalogTree } from './CatalogTree';
import { UpdateBanner } from './UpdateBanner';

/**
 * Application shell.
 *
 * Holds the three surfaces the accepted design fixes in place: the upper-left global
 * search, the tag-derived catalog, and the reading column. On a narrow viewport the
 * navigation becomes a drawer rather than squeezing the reading column, because a
 * compressed reading measure is worse than a hidden tree.
 */
export function AppShell({ rightRail }: { rightRail?: ReactNode }) {
  const { mode, setMode } = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sort, setSort] = useState('title');

  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const selectedPath = pathname.startsWith('/files/')
    ? decodeURIComponent(pathname.slice('/files/'.length))
    : null;

  const session = useQuery({ queryKey: ['session'], queryFn: () => atlas.session() });
  useEffect(() => setCsrfToken(session.data?.csrfToken ?? null), [session.data?.csrfToken]);

  const generation = useQuery({
    queryKey: ['generation'],
    queryFn: () => atlas.generation(),
    enabled: session.data?.authenticated === true,
  });

  const tree = useQuery<TreeResponse>({
    queryKey: ['tree', selectedTags],
    queryFn: () => atlas.tree(selectedTags),
    enabled: session.data?.authenticated === true,
  });

  const updates = useGenerationUpdates(generation.data?.generation?.generationId ?? null);

  // Route changes close the mobile drawer; leaving it open would cover what was chosen.
  useEffect(() => setDrawerOpen(false), [pathname]);

  const toggleTag = (tagId: string) =>
    setSelectedTags((current) =>
      current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId],
    );

  if (session.isLoading) {
    return <div className="search-state-msg">Checking your session…</div>;
  }

  if (!session.data?.authenticated) {
    return <SignedOut />;
  }

  return (
    <>
      <a className="atlas-skip-link" href="#atlas-main">
        Skip to content
      </a>

      <header className="mobile-header">
        <button
          type="button"
          className="icon-btn"
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((open) => !open)}
        >
          <Menu size={20} />
        </button>
        <span className="mobile-title">Alphea Atlas</span>
        <ThemeToggle mode={mode} setMode={setMode} />
      </header>

      <div className="update-banner-container">
        <UpdateBanner state={updates} onRefresh={() => window.location.reload()} />
      </div>

      <div className="layout">
        <aside className={`sidebar-left${drawerOpen ? ' mobile-open' : ''}`} id="sidebar-left">
          <div>
            <div className="brand desktop-only">
              <span className="logo" aria-hidden="true">
                A
              </span>
              <h1>Alphea Atlas</h1>
            </div>

            <GlobalSearch />

            <CatalogTree
              tree={tree.data ?? null}
              loading={tree.isLoading}
              selectedPath={selectedPath}
              selectedTags={selectedTags}
              onToggleTag={toggleTag}
              sort={sort}
              onSortChange={setSort}
            />
          </div>

          <div className="auth-container">
            <div className="auth-state" style={{ display: 'flex' }}>
              <div className="avatar" aria-hidden="true">
                {session.data.principal?.displayName.slice(0, 1).toUpperCase() ?? 'A'}
              </div>
              <div className="auth-info">
                <div className="auth-name">{session.data.principal?.displayName}</div>
                <div className="auth-role">
                  <Link to="/authority">Identity &amp; authority</Link>
                </div>
              </div>
              <ThemeToggle mode={mode} setMode={setMode} />
              <button
                type="button"
                className="logout-btn"
                title="Sign out"
                aria-label="Sign out"
                onClick={async () => {
                  await atlas.logout();
                  window.location.assign('/');
                }}
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </aside>

        <main className="content-area" id="atlas-main">
          <Outlet />
        </main>

        {rightRail ? <aside className="sidebar-right">{rightRail}</aside> : null}
      </div>
    </>
  );
}

function ThemeToggle({ mode, setMode }: { mode: ThemeMode; setMode: (mode: ThemeMode) => void }) {
  const next: Record<ThemeMode, ThemeMode> = { light: 'dark', dark: 'system', system: 'light' };
  const icon = { light: <Sun size={16} />, dark: <Moon size={16} />, system: <Monitor size={16} /> }[mode];
  return (
    <button
      type="button"
      className="icon-btn"
      onClick={() => setMode(next[mode])}
      aria-label={`Theme: ${mode}. Switch to ${next[mode]}.`}
      title={`Theme: ${mode}`}
    >
      {icon}
    </button>
  );
}

/**
 * Signed-out surface.
 *
 * Says nothing about what was requested. The return path is preserved so signing in
 * lands the reader exactly where they were going, but it is never echoed back here.
 */
function SignedOut() {
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const reason = new URLSearchParams(window.location.search).get('auth');

  return (
    <div className="error-state-view">
      <div className="error-icon" aria-hidden="true">
        🔒
      </div>
      <h2>{reason === 'forbidden' ? 'Access forbidden' : 'Authentication required'}</h2>
      <p>
        {reason === 'forbidden'
          ? 'Your Okta session does not carry the group membership Atlas requires.'
          : 'Sign in to view Alphea Atlas.'}
      </p>
      {reason === 'retry' ? <p className="error-meta">Sign-in did not complete. You can try again.</p> : null}
      {reason !== 'forbidden' ? (
        <a
          className="login-btn"
          style={{ marginTop: 16, display: 'inline-block', textDecoration: 'none', maxWidth: 240 }}
          href={`/auth/login?returnTo=${encodeURIComponent(returnTo)}`}
        >
          Sign in with Okta
        </a>
      ) : null}
    </div>
  );
}
